import React, { useMemo, useState } from "react";

import {
  getCoreRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { SearchIcon } from "@/assets/icons";
import Pagination from "@/components/datasetManagement/Pagination";
import Dropdown from "@/components/ui/dropdown";
import { usePagination } from "@/hooks/usePagination";

import { twMerge } from "tailwind-merge";

import { ExtendedColumnDef } from "../datasetManagement/columns";
import LoadingState from "../datasetManagement/LoadingState";
import DashboardTable from "./DashboardTable";

import { IOptionItem } from "./dropdown/types";

const TEST_ID_PREFIX = "dashboard-list";

type StatusOptionType = {
  label: string;
  value: string;
};

interface DashboardListProps<TData, TPayload> {
  columns: ExtendedColumnDef<TData, string>[];
  dashboardData?: {
    data: TData[];
    meta: {
      pagination: {
        per_page: number;
        total: number;
        total_pages: number;
      };
    };
  };
  onParamsChange: (params: TPayload) => void;
  isLoadingData: boolean;
  statusOptions: StatusOptionType[];
  emptyDataMessage?: string | React.ReactNode;
  testId?: string;
  useExtraFilter?: boolean;
  extraFilterState?: {
    options: IOptionItem[];
    isLoading: boolean;
    disabled: boolean;
    hasMore: boolean;
    isLoadingMore: boolean;
    withSearch?: boolean;
    onLoadMore: () => void;
  };
}
function DashboardList<TData, TPayload>({
  columns,
  dashboardData = {
    data: [],
    meta: {
      pagination: { per_page: 10, total: 0, total_pages: 1 }
    }
  },
  onParamsChange,
  isLoadingData,
  statusOptions,
  emptyDataMessage,
  testId = TEST_ID_PREFIX,
  useExtraFilter = false,
  extraFilterState
}: Readonly<DashboardListProps<TData, TPayload>>) {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedExtraFilter, setSelectedExtraFilter] = useState(
    extraFilterState?.options?.[0]
  );

  const { pageSize, setPageSize, currentPage, setCurrentPage } =
    usePagination();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
    onParamsChange?.({
      page: 1,
      query: e.target.value || null
    } as TPayload);
  };
  // Table configuration
  const table = useReactTable({
    data: dashboardData.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: dashboardData.meta.pagination.total_pages,
    initialState: {
      pagination: {
        pageSize,
        pageIndex: currentPage
      },
      sorting
    },
    state: {
      sorting
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function"
          ? updater({
              pageSize,
              pageIndex: currentPage
            })
          : updater;
      onParamsChange?.({
        page: newPagination.pageIndex,
        per_page: newPagination.pageSize
      } as TPayload);
      setCurrentPage(newPagination.pageIndex);
      setPageSize(newPagination.pageSize);
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      const getOrderBy = () => {
        if (newSorting[0]) {
          return newSorting[0]?.desc ? "desc" : "asc";
        }
        return null;
      };
      onParamsChange?.({
        sort_by: newSorting[0]?.id || null,
        order_by: getOrderBy()
      } as TPayload);
      setSorting(newSorting);
    }
  });

  // Disable the input when no data is available and on the first page with default search query
  const disableInput = useMemo(
    () =>
      dashboardData?.meta?.pagination?.total === 0 &&
      currentPage === 1 &&
      !searchQuery,
    [currentPage, dashboardData, searchQuery]
  );
  const TableComponent = DashboardTable<TData>;
  const PaginationComponent = Pagination<TData>;

  return (
    <>
      <div className="flex items-center justify-between my-6">
        <div
          className={twMerge(
            "flex basis-[560px] items-center gap-1 border-[1px] border-gainsboro rounded-[12px] px-3 h-[40px]",
            disableInput ? "bg-white-smoke" : ""
          )}
        >
          <SearchIcon className="fill-light-gray w-[16px]" />
          <input
            name={`${testId}-search-input`}
            className="focus:outline-none w-full"
            placeholder="Search"
            value={searchQuery}
            disabled={disableInput}
            onChange={handleSearchChange}
            data-testid={`${testId}-search-input`}
          />
        </div>
        <div className="flex items-center gap-2">
          {useExtraFilter && extraFilterState && (
            <Dropdown
              value={selectedExtraFilter}
              data={extraFilterState.options}
              placeholder="Select Group"
              labelBorder="Group"
              className="w-[200px]"
              isLoading={extraFilterState.isLoading}
              disabled={extraFilterState.disabled}
              onSelect={(val) => {
                onParamsChange?.({
                  page: 1,
                  group_id: val?.value || null
                } as TPayload);
                setCurrentPage(1);
                setSelectedExtraFilter(val as IOptionItem);
              }}
              hasMore={extraFilterState.hasMore}
              isLoadingMore={extraFilterState.isLoadingMore}
              onLoadMore={extraFilterState.onLoadMore}
              withSearch={extraFilterState.withSearch}
              filterMatchedSearch={extraFilterState.withSearch}
            />
          )}
          <Dropdown
            className="w-[130px]"
            labelBorder="Status"
            data={statusOptions}
            defaultValue={selectedStatus}
            value={selectedStatus}
            disabled={disableInput}
            onSelect={(data) => {
              onParamsChange?.({
                page: 1,
                status: data?.value || null // using operator '||' because data?.value can be ''. If '' must return null
              } as TPayload);
              setCurrentPage(1);
              setSelectedStatus(data as StatusOptionType);
            }}
          />
        </div>
      </div>
      {isLoadingData ? (
        <LoadingState
          message="Searching..."
          testId="dashboard-list-searching-state"
        />
      ) : (
        <>
          <TableComponent table={table} emptyDataMessage={emptyDataMessage} />
          <PaginationComponent
            table={table}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            totalData={dashboardData.meta.pagination.total}
          />
        </>
      )}
    </>
  );
}

export default DashboardList;
